import React, { useState, useEffect } from "react";
import Dialog, { DialogFooter } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/Tabs";
import { updateGrievanceRemark, getGrievanceById } from "@/services/grievance";

interface EditRemarkDialogProps {
    grievance: any | null;
    isOpen: boolean;
    onClose: () => void;
    onRemarkUpdated: () => void;
}

const EditRemarkDialog: React.FC<EditRemarkDialogProps> = ({
    grievance,
    isOpen,
    onClose,
    onRemarkUpdated,
}) => {
    const [remark, setRemark] = useState("");
    const [logs, setLogs] = useState<any[]>([]);
    const [tab, setTab] = useState("edit");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(""); // ✅ For toast/error message

    // Fetch grievance by ID when dialog opens
    useEffect(() => {
        if (grievance && isOpen) {
            setLoading(true);
            getGrievanceById(grievance._id)
                .then((data) => {
                    setRemark(data.adminRemark || "");
                    setLogs(data.logs || []);
                    setError(""); // Clear any previous errors
                })
                .catch(() => {
                    setRemark("");
                    setLogs([]);
                })
                .finally(() => setLoading(false));
        }
    }, [grievance, isOpen]);

    const handleSave = async () => {
        if (!grievance) return;

        // Check for empty remark
        if (!remark.trim()) {
            setError("Remark is required");
            return;
        }

        setLoading(true);
        setError(""); // Clear previous errors
        try {
            await updateGrievanceRemark(grievance._id, remark);
            onRemarkUpdated();
            onClose();
        } catch (err: any) {
            // Handle backend errors
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError("Failed to update remark. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog isOpen={isOpen} onClose={onClose} title="Manage Remark" size="md">
            {loading ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : (
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList>
                        <TabsTrigger value="edit">Edit Remark</TabsTrigger>
                        <TabsTrigger value="logs">Logs</TabsTrigger>
                    </TabsList>

                    <TabsContent value="edit" className="mt-3">
                        {error && (
                            <div className="mb-2 text-red-600 text-sm">{error}</div> // ✅ Toast-like error
                        )}
                        <textarea
                            className="w-full border rounded p-2 text-sm"
                            rows={4}
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                            placeholder={remark ? "Update admin remark..." : "Enter admin remark..."}
                        />
                        <DialogFooter className="mt-3">
                            <Button variant="outline" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave}>Save</Button>
                        </DialogFooter>
                    </TabsContent>

                    <TabsContent value="logs" className="mt-3">
                        {logs.length > 0 ? (
                            <ul className="space-y-2 text-sm">
                                {logs.map((log, idx) => {
                                    const timestampMatch = log.match(/at (.+)$/);
                                    const timestamp = timestampMatch ? timestampMatch[1] : null;
                                    const formattedDate = timestamp
                                        ? new Date(timestamp).toLocaleString("en-GB")
                                        : "Invalid Date";

                                    return (
                                        <li key={idx} className="border-b pb-1">
                                            <div>{log}</div>
                                            <div className="text-gray-500 text-xs">{formattedDate}</div>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="text-gray-500 text-sm">No logs available</p>
                        )}
                    </TabsContent>
                </Tabs>
            )}
        </Dialog>
    );
};

export default EditRemarkDialog;
